import React, { createContext, useState, useContext } from 'react';
const colors = require('tailwindcss/colors');

const ClubContext = createContext();

const colorClassMap = {
  red: `border-l-red-400 bg-red-100 bg-opacity-75 text-red-800 border-red-400 hover:bg-red-200`,
  orange: `border-l-orange-400 bg-orange-100 bg-opacity-75 text-orange-800 border-orange-400 hover:bg-orange-200`,
  amber: `border-l-amber-400 bg-amber-100 bg-opacity-75 text-amber-800 border-amber-400 hover:bg-amber-200`,
  yellow: `border-l-yellow-400 bg-yellow-100 bg-opacity-75 text-yellow-800 border-yellow-400 hover:bg-yellow-200`,
  lime: `border-l-lime-400 bg-lime-100 bg-opacity-75 text-lime-800 border-lime-400 hover:bg-lime-200`,
  green: `border-l-green-400 bg-green-100 bg-opacity-75 text-green-800 border-green-400 hover:bg-green-200`,
  emerald: `border-l-emerald-400 bg-emerald-100 bg-opacity-75 text-emerald-800 border-emerald-400 hover:bg-emerald-200`,
  teal: `border-l-teal-400 bg-teal-100 bg-opacity-75 text-teal-800 border-teal-400 hover:bg-teal-200`,
  cyan: `border-l-cyan-400 bg-cyan-100 bg-opacity-75 text-cyan-800 border-cyan-400 hover:bg-cyan-200`,
  sky: `border-l-sky-400 bg-sky-100 bg-opacity-75 text-sky-800 border-sky-400 hover:bg-sky-200`,
  blue: `border-l-blue-400 bg-blue-100 bg-opacity-75 text-blue-800 border-blue-400 hover:bg-blue-200`,
  indigo: `border-l-indigo-400 bg-indigo-100 bg-opacity-75 text-indigo-800 border-indigo-400 hover:bg-indigo-200`,
  violet: `border-l-violet-400 bg-violet-100 bg-opacity-75 text-violet-800 border-violet-400 hover:bg-violet-200`,
  purple: `border-l-purple-400 bg-purple-100 bg-opacity-75 text-purple-800 border-purple-400 hover:bg-purple-200`,
  fuchsia: `border-l-fuschia-400 bg-fuchsia-100 bg-opacity-75 text-fuchsia-800 border-fuchsia-400 hover:bg-fuchsia-200`,
  pink: `border-l-pink-400 bg-pink-100 bg-opacity-75 text-pink-800 border-pink-400 hover:bg-pink-200`,
  rose: `border-l-rose-400 bg-rose-100 bg-opacity-75 text-rose-800 border-rose-400 hover:bg-rose-200`,
  slate: `border-l-slate-400 bg-slate-100 bg-opacity-75 text-slate-800 border-slate-400 hover:bg-slate-200`
};

// Map for converting border colors to background colors for icons
const borderToBgColorMap = {
  'border-red-400': 'bg-red-400',
  'border-orange-400': 'bg-orange-400',
  'border-amber-400': 'bg-amber-400',
  'border-yellow-400': 'bg-yellow-400',
  'border-lime-400': 'bg-lime-400',
  'border-green-400': 'bg-green-400',
  'border-emerald-400': 'bg-emerald-400',
  'border-teal-400': 'bg-teal-400',
  'border-cyan-400': 'bg-cyan-400',
  'border-sky-400': 'bg-sky-400',
  'border-blue-400': 'bg-blue-400',
  'border-indigo-400': 'bg-indigo-400',
  'border-violet-400': 'bg-violet-400',
  'border-purple-400': 'bg-purple-400',
  'border-fuschia-400': 'bg-fuchsia-400',
  'border-pink-400': 'bg-pink-400',
  'border-rose-400': 'bg-rose-400',
  'border-slate-400': 'bg-slate-400'
};

const getCategoryStyle = ({ category, court, bookingCategories }) => {
  if (!category) {
    const courtType = court ? court.split('-')[0] : 'tennis';
    const categoryInfo = bookingCategories.find(
      cat => cat.type === courtType && cat.name === 'Available'
    ) || { colour: 'slate' };
    const { colour } = categoryInfo;
    return colorClassMap[colour];
  }

  const courtType = court ? court.split('-')[0] : 'tennis';
  const categoryInfo = bookingCategories.find(
    cat => cat.type === courtType && cat.name === category
  );

  if (!categoryInfo) {
    return 'bg-slate-100 text-slate-600 border-slate-400';
  }

  const { colour } = categoryInfo;
  return colorClassMap[colour];
};

const getCategoryStyleFromList = ({ category, court, categories }) =>
  getCategoryStyle({
    category,
    court,
    bookingCategories: Array.isArray(categories) ? categories : []
  });

const getColourStyle = (colourName) => colorClassMap[colourName] || colorClassMap.slate;

// Helper function to convert border color to background color for icons
const getIconBackgroundColor = (borderColorClass) => {
  return borderToBgColorMap[borderColorClass] || 'bg-blue-400';
};

export const ClubProvider = ({ children }) => {
  // Club Details State
  const [clubDetails, setClubDetails] = useState({
    name: '',
    location: '',
    contacts: [],
    emails: [],
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    xdotcom: '',
    youtube: '',
    zapperMerchantId: '',
    terms: null
  });

  // Booking Categories State
  const [bookingCategories, setBookingCategories] = useState([]);

  // Booking Options State
  const [bookingOptions, setBookingOptions] = useState([]);

  // Court Data State
  const [courtData, setCourtData] = useState([]);

  const value = {
    // Club Details
    clubDetails,
    setClubDetails,
    
    // Booking Categories
    bookingCategories,
    setBookingCategories,

    // Booking Options
    bookingOptions,
    setBookingOptions,

    // Court Data
    courtData,
    setCourtData,

    getCategoryStyle: (params) => getCategoryStyle({ ...params, bookingCategories }),
    getCategoryStyleFromList,
    getColourStyle,
    getIconBackgroundColor
  };

  return (
    <ClubContext.Provider value={value}>
      {children}
    </ClubContext.Provider>
  );
};

export const useClub = () => {
  const context = useContext(ClubContext);
  if (!context) {
    throw new Error('useClub must be used within a ClubProvider');
  }
  return context;
}; 